import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import { watchApi } from './api'

/** How long the last key press waits before it is sent, so holding an arrow sends once. A drag sends once anyway. */
export const SOURCE_VOLUME_SEND_MS = 300
/** How long a level the API took waits for the room's metadata to carry it before the room's level shows again. */
export const SOURCE_VOLUME_CONFIRM_MS = 5000

export interface SourceVolumeControls {
  /**
   * The level this tab plays and shows, 0–100: the one the host is setting while their drag or its request is under
   * way, else the stage's (`roomLevel`).
   */
  level: number
  /** The slider moved: heard on this tab at once, nothing sent. */
  preview(level: number): void
  /** The drag or key press ended: sent to the stage, and the level holds until the room's metadata moves. */
  commit(level: number): void
  /** Why the last change didn't go through, in the dock's words; null otherwise. */
  error: string | null
}

const FAILED = 'We couldn’t change the volume. Try again.'

/** Why a change was refused: a 429 says when to try again (60 a minute), anything else to try again. */
function failureWords(failure: unknown): string {
  const refused = apiFailure(failure)
  const wait = refused.status === 429 ? waitWords(refused.retryAfterMs) : null
  return wait ? `That’s a lot of changes. Try again in ${wait}.` : FAILED
}

/**
 * A live host's level for the source under their voice on `stageId` (docs/features/stage-volume.md). The stage's level
 * arrives in the room metadata (`roomLevel`); the host's own tab hears a drag as it moves (`preview`) and sends the level
 * once it is let go (`commit`, `PUT /stages/:id/source_volume`). What this tab set holds until the room's level moves:
 * to what it sent (a newer level the host is still setting holds on), or to another host's, which wins. A refusal goes
 * back to the room's and says why (`error`); a level the API took but the room never carries shows the room's again
 * after `SOURCE_VOLUME_CONFIRM_MS`, so the host never hears a level their audience doesn't.
 */
export function useSourceVolume(stageId: string, roomLevel: number): SourceVolumeControls {
  const [pending, setPending] = useState<{ stageId: string; level: number; room: number } | null>(null)
  const [error, setError] = useState<{ stageId: string; words: string } | null>(null)
  // The levels this tab sent that the room may still carry back, so none is taken for another host's change.
  const [sent, setSent] = useState<readonly { request: number; stageId: string; level: number }[]>([])
  let held = pending?.stageId === stageId ? pending : null
  if (held && held.room !== roomLevel) {
    const echo = sent.some((level) => level.stageId === stageId && level.level === roomLevel)
    held = echo && held.level !== roomLevel ? { ...held, room: roomLevel } : null
  }
  if (held !== pending) setPending(held)
  const level = held ? held.level : roomLevel

  const latest = useRef({ stageId, roomLevel })
  useEffect(() => {
    latest.current = { stageId, roomLevel }
  })
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requests = useRef(0)
  const queued = useRef<(() => void) | null>(null)

  const preview = useCallback((next: number) => {
    setError(null)
    setPending({ stageId: latest.current.stageId, level: next, room: latest.current.roomLevel })
  }, [])

  const commit = useCallback(
    (next: number) => {
      preview(next)
      const target = latest.current.stageId
      const send = () => {
        timer.current = null
        queued.current = null
        const request = ++requests.current
        const settled = () => setSent((levels) => levels.filter((level) => level.request !== request))
        setSent((levels) => [...levels, { request, stageId: target, level: next }])
        watchApi
          .setSourceVolume(target, next)
          .then(() => {
            setTimeout(() => {
              settled()
              setPending((current) => (current?.stageId === target && current.level === next ? null : current))
            }, SOURCE_VOLUME_CONFIRM_MS)
          })
          .catch((failure: unknown) => {
            settled()
            // A later change answers for itself.
            if (request !== requests.current) return
            setPending(null)
            setError({ stageId: target, words: failureWords(failure) })
          })
      }
      if (timer.current) clearTimeout(timer.current)
      queued.current = send
      timer.current = setTimeout(send, SOURCE_VOLUME_SEND_MS)
    },
    [preview],
  )

  // Leaving (another page, the end of the live) still sends a change that was let go.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
      queued.current?.()
    },
    [],
  )

  const words = error && error.stageId === stageId ? error.words : null
  return useMemo(() => ({ level, preview, commit, error: words }), [level, preview, commit, words])
}
